/**
 * Survive browser translation (Google Translate in Chrome, and others like it).
 *
 * A translator replaces each text node on the page with its own `<font>`
 * wrappers holding the translated words. React still holds the original text
 * nodes, which are no longer in the document, so the next update that removes
 * one — or inserts an element in front of one, like a spinner appearing in a
 * button — throws:
 *
 *   NotFoundError: Failed to execute 'insertBefore' on 'Node': The node before
 *   which the new node is to be inserted is not a child of this node.
 *
 * and the whole screen is replaced by an error. Reproduced by wrapping every
 * text node the way the translator does and pressing "Send the notetaker".
 *
 * This is the long-standing workaround from the React issue tracker
 * (facebook/react#11538): make the two operations tolerate a reference node
 * that has been moved out from under them.
 *
 *  - `removeChild` of a node that is not a child: it is already gone from
 *    where React thinks it is, so there is nothing to remove.
 *  - `insertBefore` relative to a node that is not a child: append instead.
 *    The new element appears, possibly not in its exact position — a visible
 *    error message slightly out of order beats a missing one, and both beat a
 *    blank screen.
 *
 * The interface is translated properly now (English and French), so far fewer
 * people need the browser's translator — but anyone reading in a third
 * language still will, and they get a working app either way.
 */
export function installDomGuard() {
  if (typeof Node !== "function" || !Node.prototype) return;

  const removeChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      return child;
    }
    return removeChild.call(this, child) as T;
  };

  const insertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      return insertBefore.call(this, newNode, null) as T;
    }
    return insertBefore.call(this, newNode, referenceNode) as T;
  };
}
